import { isDevelopment, env } from '@diana/env-ui';
import { IFiltersValues, CalendarDatePickerPreset, presetToValue } from '@diana/core';
import { IExtendedFiltersValues } from './convertFiltersToQuery';
import { FilterType } from '../stores/useFilters';
import { createBrowserHistory } from 'history';

const history = createBrowserHistory();

export const getOrigin = (): string => {
  return isDevelopment() || isCloud() ? env('CLOUD_API_ENDPOINT') : window.location.origin;
};

export const isCloud = (): boolean => {
  return window.location.host === env('CLOUD_HOST');
};

export const getFullUrl = (endpoint: string): string => {
  return `${getOrigin()}${endpoint}`;
};

export const SIGNALR_STREAM_DELIVERY_SUMMARY = getFullUrl('/data-logger/hub/mfm-delivery-summary');
export const SIGNALR_STREAM_DELIVERY_ENDPOINT = getFullUrl('/data-logger/hub/mfm-delivery-profile');
export const SIGNALR_STREAM_ALARMS_ENDPOINT = getFullUrl('/data-logger/hub/mfm-alerts');
export const SIGNALR_STREAM_FILTER_OPTIONS = getFullUrl('/data-logger/hub/options');
export const SIGNALR_STREAM_MPA_TRANSMISSION = getFullUrl('/data-logger/hub/transmission');

export const hasTimeRangeInQuery = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  const hasPreset = urlParams.has('preset');
  const hasFromTo = urlParams.has('from') || urlParams.has('to');

  return hasPreset || hasFromTo;
};

export const checkFiltersInQuery = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);

  // Check each filter type for presence
  return Object.values(FilterType).some((filterType) => {
    const filterValue = urlParams.get(filterType);
    return filterValue !== null && filterValue.trim() !== ''; // Ensure the value exists and isn't empty
  });
};

export const buildQueryParams = (filtersValues: IExtendedFiltersValues): URL => {
  const url = new URL(window.location.toString());
  //TODO: Temporary commented, need to refactor URLs after new bunkenring dashboards

  // if (filtersValues[FilterType.BARGES] && filtersValues[FilterType.BARGES].length > 0) {
  //   const barges = filtersValues[FilterType.BARGES].map((item) => item.displayName).join(',');
  //   url.searchParams.set(FilterType.BARGES, barges);
  // } else {
  //   url.searchParams.delete(FilterType.BARGES);
  // }

  if (filtersValues[FilterType.MFM_DEVICES] && filtersValues[FilterType.MFM_DEVICES].length > 0) {
    const mfmDeviceValues = filtersValues[FilterType.MFM_DEVICES]
      .map((item) => item.value)
      .join(',');
    url.searchParams.set(FilterType.MFM_DEVICES, mfmDeviceValues);
  } else {
    url.searchParams.delete(FilterType.MFM_DEVICES);
  }

  if (filtersValues[FilterType.BMT_NUMBER] && filtersValues[FilterType.BMT_NUMBER].length > 0) {
    const bmtNumberValues = filtersValues[FilterType.BMT_NUMBER]
      .map((item) => item.displayName)
      .join(',');
    url.searchParams.set(FilterType.BMT_NUMBER, bmtNumberValues);
  } else {
    url.searchParams.delete(FilterType.BMT_NUMBER);
  }

  if (filtersValues[FilterType.TIME_RANGE] && filtersValues[FilterType.TIME_RANGE].length > 0) {
    filtersValues.timeRange.forEach((item, i) => {
      if (item.preset) {
        url.searchParams.set('preset', item.preset);
        url.searchParams.delete(`${i === 0 ? 'from' : 'to'}`);
      } else if (item.value !== null) {
        url.searchParams.delete('preset');
        url.searchParams.set(`${i === 0 ? 'from' : 'to'}`, item.value as string);
      } else {
        url.searchParams.delete('preset');
        url.searchParams.delete(`${i === 0 ? 'from' : 'to'}`);
      }
    });
  } else {
    url.searchParams.delete('preset');
    url.searchParams.delete('from');
    url.searchParams.delete('to');
  }

  return url;
};

export const updateBrowserQueryParams = (filtersValues: IFiltersValues) => {
  const updatedUrl = buildQueryParams(filtersValues);
  history.replace(updatedUrl.pathname + updatedUrl.search);
};

export const parseQueryToFilters = (): IExtendedFiltersValues => {
  const urlParams = new URLSearchParams(window.location.search);
  const filters: IExtendedFiltersValues = {
    timeRange: [],
    [FilterType.MFM_DEVICES]: [],
  };

  const preset = urlParams.get('preset') as CalendarDatePickerPreset | null;
  const from = urlParams.get('from');
  const to = urlParams.get('to');

  if (preset) {
    filters.timeRange = [
      {
        preset,
        value: presetToValue({ preset, timezone: 'UTC' })?.[0] || '',
        displayName: '',
      },
      {
        preset,
        value: presetToValue({ preset, timezone: 'UTC' })?.[1] || '',
        displayName: '',
      },
    ];
  } else if (from || to) {
    filters.timeRange = [
      {
        value: from || '',
        displayName: from || '',
        preset: undefined,
      },
      {
        value: to || '',
        displayName: to || '',
        preset: undefined,
      },
    ];
  }

  if (urlParams.get(FilterType.MFM_DEVICES)) {
    filters[FilterType.MFM_DEVICES] = (urlParams.get(FilterType.MFM_DEVICES)?.split(',') ?? []).map((id) => ({
      value: id,
      displayName: id,
    }));
  }

  return filters;
};
